import { ErrorChip } from '@starsoft/common/components';
import { useKycMeByOriginIdWithOrigin } from '@starsoft/common/hooks';
import { GenericError } from '@starsoft/common/models';
import { extractFirstAndLastName } from '@starsoft/common/utils/user-identity';
import { AxiosError } from 'axios';
import { UserPaymentDetailsFormContentUserKycCardProps } from './props';
import UserPaymentDetailsFormContentUserKycCardSkeleton from './Skeleton';
import UserPaymentDetailsFormContentUserKycCardStatus from './Status';
import styles from './styles.module.scss';

/**
 * UserPaymentDetailsFormContentUserKycCard Component
 * This component is responsible for displaying the user's KYC (Know Your Customer) card.
 * It handles different states such as loading, error, and displays the user's identity information.
 *
 * @param {UserPaymentDetailsFormContentUserKycCardProps} props - The properties for the component.
 * @param {string} props.image - The URL or path to the user's image to be displayed.
 * @param {boolean} props.isLoading - Flag indicating whether the data is still loading.
 *
 * @returns {JSX.Element} The rendered component displaying the user's KYC card, a skeleton if loading, or an error chip if an error occurs.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsFormContentUserKycCard image="path/to/image.jpg" isLoading={false} />
 */
export default function UserPaymentDetailsFormContentUserKycCard({
  image,
  isLoading,
}: UserPaymentDetailsFormContentUserKycCardProps) {
  // Destructure the necessary values from the custom hook for KYC data
  const {
    isPending,
    error: kycError,
    kyc,
    refetch: refetchKyc,
  } = useKycMeByOriginIdWithOrigin();
  const error: AxiosError<GenericError> = kycError;

  const { firstName, lastName } = extractFirstAndLastName(kyc?.identity?.name);

  /**
   * handleError Function
   * This function handles the error state by refetching the KYC data if an error is present.
   */
  function handleError() {
    if (kycError) {
      refetchKyc();
    }
  }

  // Render an error chip if there is an error
  if (error) {
    return <ErrorChip action={handleError} />;
  }

  // Render a skeleton component if the data is pending or loading
  if (isPending || isLoading) {
    return <UserPaymentDetailsFormContentUserKycCardSkeleton />;
  }

  // Render the user's KYC card with identity information
  return (
    <div className={styles.card}>
      <div className={styles.card__row}>
        <div className={styles.card__row__wrapper}>
          {/* <Image
            width={40}
            height={40}
            className={styles.card__row__wrapper__image}
            src={image}
            alt="profile"
          /> */}
          <div className={styles.card__row__wrapper__column}>
            <div className={styles.card__row__wrapper__label}>
              {firstName} {lastName}
            </div>
            <div className={styles.card__row__wrapper__description}>
              {kyc?.identity?.maskedIdentifier}
            </div>
          </div>
        </div>
        <UserPaymentDetailsFormContentUserKycCardStatus status={kyc?.status} />
      </div>
    </div>
  );
}
